import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ImpressumScreen from './ImpressumScreen';

const { FiArrowLeft, FiArrowRight } = FiIcons;

const QuestionScreen = ({ question, currentQuestion, totalQuestions, onAnswer, onBack, onNext, selectedAnswers }) => {
  const [mostSelected, setMostSelected] = useState(null);
  const [leastSelected, setLeastSelected] = useState(null);
  const [showImpressum, setShowImpressum] = useState(false);
  const questionIdRef = useRef(question.id);

  // Only reset when question actually changes (navigation), not on every render
  useEffect(() => {
    if (questionIdRef.current !== question.id) {
      // New question - reset selections
      setMostSelected(null);
      setLeastSelected(null);
      questionIdRef.current = question.id;
    } else if (selectedAnswers) {
      // Same question but with existing answers - restore them
      setMostSelected(selectedAnswers.most);
      setLeastSelected(selectedAnswers.least);
    }
  }, [question.id, selectedAnswers]);

  if (showImpressum) {
    return <ImpressumScreen onBack={() => setShowImpressum(false)} />;
  }

  const handleMostSelect = (index) => {
    // If selecting the same as least, clear least selection
    if (index === leastSelected) {
      setLeastSelected(null);
    }
    
    // If clicking the same selected most option, deselect it
    if (index === mostSelected) {
      setMostSelected(null);
      updateAnswer(null, leastSelected);
    } else {
      setMostSelected(index);
      updateAnswer(index, leastSelected);
    }
  };

  const handleLeastSelect = (index) => {
    // If selecting the same as most, clear most selection
    if (index === mostSelected) {
      setMostSelected(null);
    }
    
    // If clicking the same selected least option, deselect it
    if (index === leastSelected) {
      setLeastSelected(null);
      updateAnswer(mostSelected, null);
    } else {
      setLeastSelected(index);
      updateAnswer(mostSelected, index);
    }
  };

  const updateAnswer = (most, least) => {
    // Only update answer if both selections are made
    if (most !== null && least !== null) {
      onAnswer({
        questionId: question.id,
        most: most,
        least: least,
        mostType: question.options[most].type,
        leastType: question.options[least].type
      });
    } else {
      // If one selection is cleared, update with null for the missing one
      onAnswer({
        questionId: question.id,
        most: most,
        least: least,
        mostType: most !== null ? question.options[most].type : null,
        leastType: least !== null ? question.options[least].type : null
      });
    }
  };

  const canProceed = mostSelected !== null && leastSelected !== null;
  const progress = (currentQuestion / totalQuestions) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">DISC Persönlichkeitstest</h1>
            <span className="text-gray-600 font-medium">
              Frage {currentQuestion} von {totalQuestions}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3">
            <motion.div 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl shadow-xl p-8"
        >
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Welche Beschreibung trifft am meisten bzw. am wenigsten auf Sie zu?
            </h2>
            <p className="text-gray-600">
              Bitte wählen Sie aus, was am meisten und was am wenigsten zutrifft.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Most Column */}
            <div>
              <h3 className="text-lg font-semibold text-green-600 mb-4 text-center">
                Trifft am meisten zu
              </h3>
              <div className="space-y-3">
                {question.options.map((option, index) => {
                  const isDisabled = index === leastSelected;
                  const isSelected = index === mostSelected;
                  
                  return (
                    <motion.button
                      key={`most-${index}`}
                      whileHover={{ scale: isDisabled ? 1 : 1.02 }}
                      whileTap={{ scale: isDisabled ? 1 : 0.98 }}
                      onClick={() => !isDisabled && handleMostSelect(index)}
                      disabled={isDisabled}
                      className={`w-full p-4 rounded-xl text-left font-medium transition-all duration-300 ${
                        isSelected
                          ? 'bg-green-100 border-2 border-green-500 text-green-800'
                          : isDisabled
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent text-gray-700'
                      }`}
                    >
                      {option.text}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Least Column */}
            <div>
              <h3 className="text-lg font-semibold text-red-600 mb-4 text-center">
                Trifft am wenigsten zu
              </h3>
              <div className="space-y-3">
                {question.options.map((option, index) => {
                  const isDisabled = index === mostSelected;
                  const isSelected = index === leastSelected;
                  
                  return (
                    <motion.button
                      key={`least-${index}`}
                      whileHover={{ scale: isDisabled ? 1 : 1.02 }}
                      whileTap={{ scale: isDisabled ? 1 : 0.98 }}
                      onClick={() => !isDisabled && handleLeastSelect(index)}
                      disabled={isDisabled}
                      className={`w-full p-4 rounded-xl text-left font-medium transition-all duration-300 ${
                        isSelected
                          ? 'bg-red-100 border-2 border-red-500 text-red-800'
                          : isDisabled
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent text-gray-700'
                      }`}
                    >
                      {option.text}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Selection Status */}
          <div className="mt-6 p-4 bg-blue-50 rounded-xl text-center">
            <p className="text-sm text-blue-800">
              {mostSelected !== null && leastSelected !== null 
                ? `Auswahl vervollständigt: "${question.options[mostSelected].text}" (meist) und "${question.options[leastSelected].text}" (wenigst)`
                : mostSelected !== null
                ? `Sie haben "${question.options[mostSelected].text}" als "trifft am meisten zu" ausgewählt. Bitte wählen Sie noch "trifft am wenigsten zu".`
                : leastSelected !== null
                ? `Sie haben "${question.options[leastSelected].text}" als "trifft am wenigsten zu" ausgewählt. Bitte wählen Sie noch "trifft am meisten zu".`
                : 'Bitte wählen Sie je eine Option für "trifft am meisten zu" und "trifft am wenigsten zu".'
              }
            </p>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={onBack}
              disabled={currentQuestion === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                currentQuestion === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              <span>Zurück</span>
            </button>

            <button
              onClick={onNext}
              disabled={!canProceed}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                canProceed
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span>{currentQuestion === totalQuestions ? 'Auswertung' : 'Weiter'}</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

        {/* Impressum Link */}
        <div className="text-center mt-8">
          <button
            onClick={() => setShowImpressum(true)}
            className="text-sm text-gray-500 hover:text-blue-600 underline transition-colors"
          >
            Impressum
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionScreen;